import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, clock, countdown, dateLabel, durationLabel, usd } from '../api/client';
import {
  useAddPatrolNote,
  useCorrectOrderPlate,
  useLiveSessions,
  useOrderActivity,
  useOrders,
  usePlateLookup,
  useRefundOrder,
} from '../api/hooks';
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  PageHeader,
  Panel,
  Table,
  Td,
  Th,
} from '../components/ui';
import { useApiError } from '../i18n/useApiError';

/**
 * Orders, the live board and plate lookup.
 *
 * Lookup is explicitly a manual spot check, not enforcement — V1 has no LPR and
 * no gate integration, and the console says so out loud (FR-A-26 ~ A-28).
 */
export function OrdersPage({
  lotId,
  canEdit,
  canRefund,
}: {
  lotId?: string;
  canEdit: boolean;
  canRefund: boolean;
}) {
  const { t } = useTranslation();
  const live = useLiveSessions(lotId);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [plateFilter, setPlateFilter] = useState('');
  const orders = useOrders({
    lotId,
    from: from || undefined,
    to: to || undefined,
    plate: plateFilter || undefined,
  });

  const [plate, setPlate] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const lookup = usePlateLookup();

  const exportCsv = () => {
    const query = new URLSearchParams({ format: 'csv' });
    if (lotId) query.set('lotId', lotId);
    if (from) query.set('from', from);
    if (to) query.set('to', to);
    void api.download(`/admin/orders?${query.toString()}`, 'parkingpay-orders.csv');
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('orders.title')}
        hint={t('orders.hint')}
        actions={<Button onClick={exportCsv}>{t('orders.exportCsv')}</Button>}
      />

      <div className="grid gap-5 lg:grid-cols-[1.25fr_0.75fr]">
        <div className="min-w-0 flex flex-col gap-4">
          <div className="flex items-baseline justify-between">
            <h3 className="text-[13px] font-bold">
              {t('orders.inProgress', { count: live.data?.count ?? 0 })}
            </h3>
            <span className="text-[11px] text-muted">{t('orders.autoRefresh')}</span>
          </div>

          {live.isPending ? (
            <Loading />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('orders.colPlate')}</Th>
                  <Th>{t('orders.colSpace')}</Th>
                  <Th>{t('orders.colLot')}</Th>
                  <Th>{t('orders.colStart')}</Th>
                  <Th>{t('orders.colExpiry')}</Th>
                  <Th align="right">{t('orders.colRemaining')}</Th>
                  <Th align="right">{t('orders.colPaid')}</Th>
                </tr>
              </thead>
              <tbody>
                {live.data?.sessions.map((s) => (
                  <tr key={s.id}>
                    <Td mono strong>
                      {s.plate}
                    </Td>
                    <Td mono>{s.spaceNumber ?? '—'}</Td>
                    <Td>{s.lotName}</Td>
                    <Td mono>{clock(s.startedAt)}</Td>
                    <Td mono>{clock(s.expiresAt)}</Td>
                    <Td align="right" mono>
                      {countdown(s.remainingSeconds)}
                    </Td>
                    <Td align="right" mono>
                      {usd(s.paidTotalCents)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <h3 className="mt-2 text-[13px] font-bold">{t('orders.history')}</h3>
          <div className="grid gap-2 sm:grid-cols-3">
            <Field label={t('orders.from')}>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label={t('orders.to')}>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <Field label={t('orders.plateFilter')}>
              <Input
                value={plateFilter}
                onChange={(e) => setPlateFilter(e.target.value.toUpperCase())}
                placeholder="K92 8471"
              />
            </Field>
          </div>

          {orders.isPending ? (
            <Loading />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('orders.colRef')}</Th>
                  <Th>{t('orders.colPlate')}</Th>
                  <Th>{t('orders.colSpace')}</Th>
                  <Th>{t('orders.colTime')}</Th>
                  <Th>{t('orders.colDuration')}</Th>
                  <Th align="right">{t('orders.colPaid')}</Th>
                  <Th align="right">{t('orders.colRefunded')}</Th>
                  <Th align="right">{t('orders.colActions')}</Th>
                </tr>
              </thead>
              <tbody>
                {orders.data?.orders.map((order) => (
                  <tr key={order.id}>
                    <Td mono>{order.reference}</Td>
                    <Td mono strong>
                      {order.plate}
                    </Td>
                    <Td mono>{order.spaceNumber ?? '—'}</Td>
                    <Td mono>
                      {dateLabel(order.startedAt)} {clock(order.startedAt)}
                    </Td>
                    <Td>
                      {durationLabel(order.totalMinutes, t)}
                      {order.extensionCount > 0
                        ? t('orders.withExtensions', { count: order.extensionCount })
                        : ''}
                    </Td>
                    <Td align="right" mono>
                      {usd(order.paidTotalCents)}
                    </Td>
                    <Td align="right" mono>
                      {order.refundedCents > 0 ? usd(order.refundedCents) : '—'}
                    </Td>
                    <Td align="right">
                      <Button onClick={() => setSelectedId(order.id)}>{t('orders.manage')}</Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        <Card className="flex h-fit flex-col gap-3">
          <h3 className="text-[13px] font-bold">{t('orders.lookupTitle')}</h3>
          <Input
            className="num text-lg tracking-[2px]"
            value={plate}
            placeholder="K92 8471"
            onChange={(e) => setPlate(e.target.value.toUpperCase())}
          />
          <Button variant="dark" onClick={() => lookup.mutate(plate)} disabled={plate.length < 2}>
            {t('orders.lookup')}
          </Button>

          {lookup.isError ? <ErrorNote message={t('orders.lookupFailed')} /> : null}

          {lookup.data ? (
            lookup.data.paid && lookup.data.session ? (
              <div className="flex flex-col gap-1 rounded-xl bg-success p-3 text-white">
                <span className="text-[15px] font-bold">{t('orders.paidOk')}</span>
                <span className="num text-xs opacity-90">
                  {t('orders.paidOkDetail', {
                    lot: lookup.data.session.lotName,
                    time: clock(lookup.data.session.expiresAt),
                    remaining: countdown(lookup.data.session.remainingSeconds),
                  })}
                </span>
              </div>
            ) : (
              <div className="rounded-xl bg-danger p-3 text-white">
                <span className="text-[15px] font-bold">{t('orders.notPaid')}</span>
              </div>
            )
          ) : null}

          <Panel>{t('orders.panel')}</Panel>
        </Card>
      </div>

      <OrderActions
        key={selectedId ?? 'closed'}
        sessionId={selectedId}
        plate={orders.data?.orders.find((order) => order.id === selectedId)?.plate ?? ''}
        canEdit={canEdit}
        canRefund={canRefund}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

function OrderActions({
  sessionId,
  plate,
  canEdit,
  canRefund,
  onClose,
}: {
  sessionId: string | null;
  plate: string;
  canEdit: boolean;
  canRefund: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const activity = useOrderActivity(sessionId);
  const refund = useRefundOrder();
  const correct = useCorrectOrderPlate();
  const addNote = useAddPatrolNote();
  const [refundReason, setRefundReason] = useState('');
  const [newPlate, setNewPlate] = useState(plate);
  const [plateState, setPlateState] = useState('IL');
  const [correctionReason, setCorrectionReason] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNewPlate(plate);
    setRefundReason('');
    setCorrectionReason('');
    setNote('');
    setError(null);
  }, [plate]);

  if (!sessionId) return null;

  const run = async (operation: () => Promise<unknown>, clear: () => void) => {
    setError(null);
    try {
      await operation();
      clear();
    } catch (cause) {
      setError(describeError(cause, 'orders.actionFailed'));
    }
  };

  const alreadyRefunded = activity.data?.refunds.some((item) => item.status !== 'failed') ?? false;

  return (
    <Card className="flex flex-col gap-4 border-ink">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-bold">{t('orders.manageTitle')}</h3>
          <p className="num text-xs text-muted">
            {plate} · #{sessionId.slice(0, 8).toUpperCase()}
          </p>
        </div>
        <Button variant="ghost" onClick={onClose}>
          {t('orders.close')}
        </Button>
      </div>
      {error ? <ErrorNote message={error} /> : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <section className="flex flex-col gap-2 border-t border-hairline pt-3">
          <h4 className="text-[13px] font-bold">{t('orders.patrolNote')}</h4>
          <textarea
            rows={3}
            value={note}
            disabled={!canEdit}
            onChange={(event) => setNote(event.target.value)}
            className="rounded-lg border border-hairline px-3 py-2 text-[13px]"
          />
          <Button
            disabled={!canEdit || note.trim().length < 2 || addNote.isPending}
            onClick={() =>
              void run(
                () => addNote.mutateAsync({ sessionId, note }),
                () => setNote(''),
              )
            }
          >
            {t('orders.addNote')}
          </Button>
        </section>

        <section className="flex flex-col gap-2 border-t border-hairline pt-3">
          <h4 className="text-[13px] font-bold">{t('orders.correctPlate')}</h4>
          <div className="grid grid-cols-[1fr_70px] gap-2">
            <Input
              value={newPlate}
              disabled={!canEdit}
              onChange={(event) => setNewPlate(event.target.value.toUpperCase())}
            />
            <Input
              value={plateState}
              maxLength={2}
              disabled={!canEdit}
              onChange={(event) => setPlateState(event.target.value.toUpperCase())}
            />
          </div>
          <Input
            placeholder={t('orders.reasonRequired')}
            value={correctionReason}
            disabled={!canEdit}
            onChange={(event) => setCorrectionReason(event.target.value)}
          />
          <Button
            disabled={!canEdit || newPlate.length < 2 || correctionReason.length < 5 || correct.isPending}
            onClick={() =>
              void run(
                () =>
                  correct.mutateAsync({ sessionId, plate: newPlate, plateState, reason: correctionReason }),
                () => setCorrectionReason(''),
              )
            }
          >
            {t('orders.saveCorrection')}
          </Button>
        </section>

        <section className="flex flex-col gap-2 border-t border-hairline pt-3">
          <h4 className="text-[13px] font-bold">{t('orders.refund')}</h4>
          <Input
            placeholder={t('orders.reasonRequired')}
            value={refundReason}
            disabled={!canRefund || alreadyRefunded}
            onChange={(event) => setRefundReason(event.target.value)}
          />
          <Button
            variant="dark"
            disabled={!canRefund || alreadyRefunded || refundReason.length < 5 || refund.isPending}
            onClick={() =>
              void run(
                () => refund.mutateAsync({ sessionId, reason: refundReason }),
                () => setRefundReason(''),
              )
            }
          >
            {alreadyRefunded ? t('orders.refunded') : t('orders.refundFull')}
          </Button>
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="flex flex-col gap-2">
          <h4 className="text-[12px] font-bold">{t('orders.refundHistory')}</h4>
          {activity.data?.refunds.length ? (
            activity.data.refunds.map((item) => (
              <p key={item.id} className="text-xs text-muted">
                {new Date(item.createdAt).toLocaleString()} · {usd(item.amountCents)} · {item.reason}
              </p>
            ))
          ) : (
            <p className="text-xs text-faint">{t('orders.none')}</p>
          )}
        </section>
        <section className="flex flex-col gap-2">
          <h4 className="text-[12px] font-bold">{t('orders.patrolHistory')}</h4>
          {activity.data?.notes.length ? (
            activity.data.notes.map((item) => (
              <p key={item.id} className="text-xs text-muted">
                {new Date(item.createdAt).toLocaleString()} · {item.operatorName} · {item.note}
              </p>
            ))
          ) : (
            <p className="text-xs text-faint">{t('orders.none')}</p>
          )}
        </section>
      </div>
    </Card>
  );
}
